import { useContext, createContext, useState, useEffect } from "react";
import { toast } from "react-toastify";
import ApiService from "../helpers/Services";
import axios from "axios";

const TicketContext = createContext();

export const useTicketContext = () => {
  return useContext(TicketContext);
};

const retryRequest = async (fn, retries = 2, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (error) {
      if (i === retries - 1) throw error;
      console.warn(`Retrying... (${i + 1}/${retries})`);
      await new Promise((res) => setTimeout(res, delay));
    }
  }
};

export const TicketContextProvider = ({ children }) => {
  const [ticket, setTicket] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isBtnDisabled, setIsBtnDisabled] = useState(false);
  const [showToastMessage, setShowToastMessage] = useState(false);

  const getTickets = async (searchText) => {
    setLoading(true);
    try {
      const data = await retryRequest(() =>
        ApiService.post("get-task-list", {
          searchText: searchText,
        })
      );
      // Modify the status of each ticket based on the given condition
      const updatedData = data.map(ticket => ({
        ...ticket,
        displayStatus: ticket.assignedto
          ? (ticket.status.includes('Assigned') ? 'In Progress' : 'Complete')
          : 'Open'
      }));
      setTickets(updatedData);
    } catch (error) {
      toast.error(error.message);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  

  const getTicketById = async (id) => {
    setLoading(true);
    try {
      const data = await retryRequest(() =>
        ApiService.post("get-task", {
          workflowId: id,
        })
      );
      setTicket(data);
      return data
    } catch (error) {
      toast.error("Oops something went wrong while fetching task.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const lockTicketById = async (id, forceLock) => {
    setLoading(true);
    try {
      const data = await retryRequest(() =>
        ApiService.post("assign-task", {
          workflowId: id,
          forceLock: forceLock
        })
      );
      setTicket(data);
      return data
    } catch (error) {
      toast.error("Oops something went wrong while fetching task.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (msg) => {
    setIsBtnDisabled(true);
    try {
      await retryRequest(() =>
        ApiService.post("send-message", {
          workflowId: ticket?.id,
          type: "message",
          message: msg,
        })
      );
    } catch (error) {
      toast.error("Oops something went wrong while sending message.");
      console.error(error);
    } finally {
      setIsBtnDisabled(false);
    }
  };

  const sendMessageWithDoc = async (s3LocationPath) => {
    setIsBtnDisabled(true);
    try {
      await retryRequest(() =>
        ApiService.post("send-message", {
          workflowId: ticket?.id,
          type: "doc",
          message: s3LocationPath,
        })
      );
    } catch (error) {
      toast.error("Oops something went wrong while sending message.");
      console.error(error);
    } finally {
      setIsBtnDisabled(false);
    }
  };

  const uploadFile = async (file, endpoint) => {
    setIsBtnDisabled(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      console.log({ file });
      await retryRequest(() =>
        axios.put(endpoint, file, {
          headers: {
            "Content-Type": file.type,
          },
        })
      );
    } catch (error) {
      toast.error("Oops something went wrong while uploading file.");
      console.error(error);
      throw new Error("Something went wrong while uploading the document.");
    } finally {
      setIsBtnDisabled(false);
    }
  };

  const createSignedUrl = async (file) => {
    setIsBtnDisabled(true);
    try {
      const filename = file.name ?? "new.pdf";
      const data = await retryRequest(() =>
        ApiService.post("create-signed-url", {
          workflowId: ticket?.id,
          filename: filename,
          contentType: file.type,
        })
      );
      console.log({ data });
      await uploadFile(file, data?.signedURL);
      const docResp = await sendMessageWithDoc(data?.location);
      console.log({ docResp });
      await getTicketById(ticket.id);
    } catch (error) {
      toast.error("Oops something went wrong while creating signed URL.");
      console.error(error);
    } finally {
      setIsBtnDisabled(false);
    }
  };

  const completeWorkFlow = async (assignTo, status, comment, refreshTicket, metadata) => {
    setLoading(true);
    let payload = {}
    try {
      if (assignTo) {
        payload = {
          workflowId: ticket.id,
          ...({"assignTo": assignTo, status: `${assignTo} Assigned`}),
        }

      } else {
        payload = {
          workflowId: ticket.id,
          ...({
            status: status,
            comment: comment,
            metadata: metadata
          }),
        }

      }
      const data = await retryRequest(() =>
        ApiService.post("progress-workflow", payload)
      );
      setShowToastMessage(true);
      if (refreshTicket)
        await getTicketById(ticket.id);

      console.log({ data });
      return '1';
    } catch (error) {
      toast.error(error);
      console.error(error);
      setShowToastMessage(false);
      setLoading(false);
      return '0';
    } 
  };

  const checkProcessHandler = async (status, formData) => {
    setLoading(true);
    try {
      const data = await retryRequest(() =>
        ApiService.post("progress-workflow", {
          workflowId: ticket?.id,
          status: status,
          ...(formData && { instructions: formData }),
        })
      );

      console.log({ data });
    } catch (error) {
      toast.error("Oops something went wrong while completing the workflow.");
      console.error(error);
      return '0';
    } finally {
      setLoading(false);
    }
  };

  const rejectWithCorrespondingHandler = async (file, metadata) => {
    setLoading(true);
    try {
      const filename = file.name ?? "new.pdf";
      const data = await retryRequest(() =>
        ApiService.post("create-signed-url", {
          workflowId: ticket?.id,
          filename: filename,
          contentType: file.type,
        })
      );

      await uploadFile(file, data?.signedURL);
      await Promise.all([
        sendMessageWithDoc(data?.location)
      ]);      
      await checkProcessHandler("Reject With Correspondence", {
        comment: metadata.comment
      });
      await getTicketById(ticket.id);
    } catch (error) {
      toast.error("Oops something went wrong while rejecting with correspondence.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const rescanCheckRequestHandler = async (status, metadata) => {
    setLoading(true);
    try {

      await checkProcessHandler("Rescan", {
        comment: metadata?.comment,
        reason: metadata?.reason,
        newLob: metadata?.newLob
      });
      //await sendMessage(`Decision: Rescan\nReason:${metadata?.reason}\nComment: ${metadata?.comment || ''}`);
      await getTicketById(ticket.id);
    } catch (error) {
      toast.error("Oops something went wrong while completing rescan request");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  console.log({ ticket });
  return (
    <TicketContext.Provider
      value={{
        loading,
        isBtnDisabled,
        ticket,
        setTicket,
        tickets,
        getTicketById,
        getTickets,
        sendMessage,
        createSignedUrl,
        completeWorkFlow,
        setShowToastMessage,
        showToastMessage,
        checkProcessHandler,
        rejectWithCorrespondingHandler,
        rescanCheckRequestHandler,
        lockTicketById
      }}
    >
      {children}
    </TicketContext.Provider>
  );
};
